<template>
  <!-- Mobile accordion with button -->
  <div class="mb-6 md:mb-4 md:hidden" v-if="show">
    <div 
      class="rounded-3xl flex flex-col items-center transition-all duration-200"
      :class="!isSearchEnabled ? 'bg-gray-50 border-gray-200' : 'border border-[#2F3033] bg-white'"
    >
      <button
        @click="isSearchEnabled && toggleOpen()"
        :disabled="!isSearchEnabled"
        class="w-full px-4 py-3 flex items-center justify-between transition-colors rounded-3xl"
        :class="[
          !isSearchEnabled ? 'text-gray-400 cursor-not-allowed bg-gray-50' : 'text-gray-700 hover:bg-gray-50 bg-white'
        ]"
      >
        <div class="flex items-center justify-center">
          <Icon 
            name="sliders-vertical" 
            class="w-4 h-4 mr-2"
            :class="!isSearchEnabled ? 'text-gray-400' : (hasActiveFilters ? 'text-[#4ECF84]' : 'text-[#2F3033]')" 
          />
          <span class="font-semibold text-lg" :class="!isSearchEnabled ? 'text-gray-400' : ''">Refine Budget</span>
        </div>
        <div class="flex items-center gap-2">
          <span class="text-base text-gray-400 font-normal">Optional</span>
          <component
            v-if="isSearchEnabled"
            :is="open ? ChevronUp : ChevronDown"
            class="w-5 h-5 text-[#2F3033] transition-transform duration-200"
          />
        </div>
      </button>

      <div v-if="open" class="px-0 pb-2 w-full flex flex-col items-center" style="border-top: 1px solid #e0f7ef;">
        <transition-group
          name="stagger"
          tag="div"
          class="mt-0 w-full flex flex-col items-center space-y-6 pt-6 px-4"
          appear
        >
          <!-- 1. Trip Direction (Mobile) -->
          <div key="trip-direction" class="w-full pb-0">
            <h3 class="text-lg font-normal text-left mb-6">Trip Direction</h3>
            <div class="relative flex flex-row rounded-full border border-[#2F3033] bg-white shadow-sm transition-all duration-300 w-full items-center isolate">
              <div 
                class="absolute tripfinder-toggle-slider -top-[1px] -bottom-[1px] w-1/2 bg-[#2F3033] rounded-full shadow-sm transition-all duration-300 z-0 border border-gray-900/10"
                :class="localTripDirection === 'return' ? 'left-0' : 'left-1/2'"
              ></div>

              <button
                @click="emit('update:tripDirection', 'return')"
                class="relative z-10 flex-1 flex items-center justify-center px-0 py-[14px] text-lg font-semibold rounded-full transition-all duration-200 focus:outline-none whitespace-nowrap leading-tight text-center"
                :class="localTripDirection === 'return' ? 'text-white' : 'text-[#2F3033] hover:text-[#2F3033]'"
              >
                Return
              </button>
              <button
                @click="emit('update:tripDirection', 'oneway')"
                class="relative z-10 flex-1 flex items-center justify-center px-0 py-[14px] text-lg font-semibold rounded-full transition-all duration-200 focus:outline-none whitespace-nowrap leading-tight text-center"
                :class="localTripDirection === 'oneway' ? 'text-white' : 'text-[#2F3033] hover:text-[#2F3033]'"
              >
                One way
              </button>
            </div>
            <div class="w-[calc(100%+32px)] h-[1px] bg-gray-200 -ml-4 mt-6"></div>
          </div>
          
          <!-- 2. Activities (Mobile) -->
          <div key="activities" class="w-full">
            <div class="relative flex items-center justify-start mb-6">
              <h3 class="text-lg font-normal text-left">Add preferred activities</h3>
              <button
                v-if="localSelectedActivities.length > 0"
                @click="emit('update:selectedActivities', [])"
                class="absolute right-0 text-sm text-[#2F3033] hover:text-[#2F3033] underline transition-colors"
              >
                Clear
              </button>
            </div>
            <div class="w-full">
              <div class="relative flex items-center justify-start mb-6">
                <h3 class="text-lg font-normal text-left">Add preferred activities</h3>
                <button
                  v-if="localSelectedActivities.length > 0"
                  @click="emit('update:selectedActivities', [])"
                  class="absolute right-0 text-sm text-[#2F3033] hover:text-[#2F3033] underline transition-colors"
                >
                  Clear
                </button>
              </div>
              <div class="w-full">
                <TripfinderKeywordTaginput
                  v-model="localSelectedActivities"
                  :max-tags="15"
                  hide-clear-all
                />
              </div>
            </div>

            <div class="w-[calc(100%+32px)] h-[1px] bg-gray-200 -ml-4"></div>

            <!-- 4. Travel Method -->
            <div v-if="travelMethodOptions.length" class="w-full">
              <h3 class="text-lg font-normal text-left mb-6">How to get there</h3>
              <div class="grid grid-cols-3 gap-[10px]">
                <button
                  v-for="method in travelMethodOptions"
                  :key="method.id"
                  @click="toggleFromList('travelMethods', method.id)"
                  :class="[
                    'flex flex-col items-center justify-center p-2 rounded-lg border transition-all w-full aspect-square',
                    isSelected('travelMethods', method.id)
                      ? 'border-gray-200 bg-gray-100'
                      : 'border-gray-200 bg-white hover:bg-gray-50'
                  ]"
                >
                  <component :is="getTravelMethodIcon(method.id)" class="w-6 h-6 mb-1" stroke-width="1.5" />
                  <span class="text-xs font-semibold text-center leading-tight">{{ method.label }}</span>
                </button>
              </div>
            </div>

            <div class="w-[calc(100%+32px)] h-[1px] bg-gray-200 -ml-4"></div>

            <!-- 5. Accommodation -->
            <div v-if="accommodationOptions.length" class="w-full">
              <h3 class="text-lg font-normal text-left mb-6">Accommodation</h3>
              <div class="grid grid-cols-3 gap-[10px]">
                <button
                  v-for="opt in accommodationOptions"
                  :key="opt.id"
                  @click="toggleFromList('accommodations', opt.id)"
                  :class="[
                    'flex flex-col items-center justify-center p-2 rounded-lg border transition-all w-full aspect-square',
                    isSelected('accommodations', opt.id)
                      ? 'border-gray-200 bg-gray-100'
                      : 'border-gray-200 bg-white hover:bg-gray-50'
                  ]"
                >
                  <component :is="getAccommodationIcon(opt.id)" class="w-6 h-6 mb-1" stroke-width="1.5" />
                  <span class="text-xs font-semibold text-center leading-tight">{{ opt.label }}</span>
                </button>
              </div>
            </div>
          </div>
        </transition-group>
        
        <!-- Clear All Button - Mobile -->
        <div class="mt-8 pb-6 w-full flex justify-start px-4">
          <button
            @click="clearAll"
            class="w-auto h-[44px] px-6 bg-white border border-gray-200 text-gray-700 rounded-full hover:bg-gray-50 transition-colors font-semibold flex items-center justify-center whitespace-nowrap"
          >
            <Trash2 class="w-4 h-4 mr-2" />
            {{ totalSelectedCount }} filters
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Desktop 2-column layout -->
  <div v-if="open && !show" class="hidden md:flex flex-col md:w-[60%] mx-auto bg-white rounded-3xl shadow-lg border border-gray-200 min-h-[500px] max-h-[85vh] z-[70]">
    <!-- Header with Title and Close button -->
    <div 
      class="relative flex-shrink-0 flex items-center justify-center pt-6 pb-6 px-2 border-b border-gray-200"
    >
      <h3 class="text-base font-semibold text-center">Refine Budget</h3>
      <button 
        @click="$emit('update:open', false)" 
        class="absolute right-6 top-6 text-[#2F3033] hover:text-[#2F3033] transition-colors"
      >
        <X class="w-5 h-5" />
      </button>
    </div>
    
    <!-- Scrollable content area -->
    <div class="overflow-y-auto px-8 pb-12 flex-1 min-h-0">
      <div class="grid grid-cols-1 md:grid-cols-12 gap-[20px] pl-0 pr-4">
        
        <!-- Column 1: Return / One way Switch -->
        <div class="relative space-y-6 md:col-span-6">
          <div class="hidden md:block absolute -right-[10px] top-[15%] h-[70%] w-[1px] bg-gray-200"></div>
          <div>
            <h3 class="text-base font-semibold text-center mb-6 md:mt-6">Trip Direction</h3>
            <div class="relative flex flex-row rounded-full border border-[#2F3033] bg-white shadow-sm transition-all duration-300 w-full items-center isolate h-[44px]">
              <div
                class="absolute tripfinder-toggle-slider -top-[1px] -bottom-[1px] w-1/2 bg-[#2F3033] rounded-full shadow-sm transition-all duration-300 z-0 border border-gray-900/10"
                :class="tripDirection === 'return' ? 'left-0' : 'left-1/2'"
              ></div>
              <button
                @click="emit('update:tripDirection', 'return')"
                class="relative z-10 flex-1 flex items-center justify-center py-2 px-4 text-base font-semibold rounded-full transition-all duration-200 focus:outline-none whitespace-nowrap leading-tight text-center"
                :class="tripDirection === 'return' ? 'text-white' : 'text-[#2F3033] hover:text-[#2F3033]'"
              >
                Return
              </button>
              <button
                @click="emit('update:tripDirection', 'oneway')"
                class="relative z-10 flex-1 flex items-center justify-center py-2 px-4 text-base font-semibold rounded-full transition-all duration-200 focus:outline-none whitespace-nowrap leading-tight text-center"
                :class="tripDirection === 'oneway' ? 'text-white' : 'text-[#2F3033] hover:text-[#2F3033]'"
              >
                One way
              </button>
            </div>
          </div>

          <!-- Travel Method -->
          <div v-if="travelMethodOptions.length" class="border-t border-gray-100 pt-4">
            <h3 class="text-base font-semibold text-center mb-4">How to get there</h3>
            <div class="grid grid-cols-3 gap-[10px]">
              <button
                v-for="method in travelMethodOptions"
                :key="method.id"
                @click="toggleFromList('travelMethods', method.id)"
                :class="[
                  'flex flex-col items-center justify-center p-2 rounded-lg border-[1px] transition-all w-full aspect-square',
                  isSelected('travelMethods', method.id)
                    ? 'border-gray-200 bg-gray-100'
                    : 'border-gray-200 bg-white hover:bg-gray-50'
                ]"
              >
                <component :is="getTravelMethodIcon(method.id)" class="w-6 h-6 mb-1" stroke-width="1.5" />
                <span class="text-xs font-semibold text-center leading-tight">{{ method.label }}</span>
              </button>
            </div>
          </div>
        </div>
        
        <!-- Column 2: Activities -->
        <div class="relative space-y-6 md:col-span-6">
          <!-- Section: Add activities to this trip -->
          <div>
            <div class="relative flex items-center justify-center mb-6">
              <h3 class="text-base font-semibold md:mt-6">Add activities</h3>
            </div>
            <div class="w-full relative">
              <TripfinderKeywordTaginput 
                v-model="localSelectedActivities"
                :max-tags="15"
                desktop-style
                hide-clear-all
              />
              <div class="flex justify-end mt-2">
                <button
                  v-if="localSelectedActivities.length > 0"
                  @click="emit('update:selectedActivities', [])"
                  class="hidden md:block text-sm text-gray-500 hover:text-gray-700 underline transition-colors"
                >
                  Clear activities
                </button>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>

    <!-- Sticky Footer Actions -->
    <div class="pt-4 pb-6 flex gap-10 px-6 border-t border-gray-200 bg-white rounded-b-3xl justify-between">
      <button
        @click="clearAll"
        class="w-auto h-[44px] px-6 bg-white border border-gray-200 text-gray-700 rounded-full hover:bg-gray-50 transition-colors font-semibold flex items-center justify-center whitespace-nowrap"
      >
        <Trash2 class="w-4 h-4 mr-2" />
        {{ totalSelectedCount }} filters
      </button>
      <button
        @click="handleSearch"
        :disabled="!isSearchEnabled"
        :class="[
          'flex-1 md:flex-none md:w-64 h-[44px] rounded-lg transition-colors font-semibold flex items-center justify-center',
          isSearchEnabled
            ? 'bg-[#4ECF84] text-white hover:bg-[#45b875]'
            : 'bg-gray-200 text-gray-500 cursor-not-allowed opacity-50'
        ]"
      >
        Search
      </button>
    </div>
  </div>

</template>

<script setup>
import { computed, ref } from 'vue'
import TripfinderKeywordTaginput from './TripfinderKeywordTaginput.vue'
import Icon from './Icon.vue'

// Lucide Vue Next imports - semantic icons for each category
import {
  Mountain,
  Leaf,
  Building,
  Recycle,
  Heart,
  ChefHat,
  Users,
  Palette,
  ShoppingBag,
  Flame,
  Car,
  Bike,
  Plane,
  Train,
  Bus,
  Ship,
  Hotel,
  Home,
  TreePine,
  Backpack,
  Tent,
  User,
  PawPrint,
  X,
  Trash2,
  ChevronDown,
  ChevronUp,
  Waves,
  Sun,
  Snowflake,
  Palmtree,
  Building2,
  Amphora,
  HelpingHand
} from 'lucide-vue-next'

defineOptions({ name: 'BudgetEstimatorRefine' })


// Icon mapping for dynamic rendering
const getThemeIcon = (themeId) => {
  const iconMap = {
    'adventure': Mountain,
    'nature': Leaf,
    'cultural': Building,
    'eco-friendly': Recycle,
    'wellbeing': Heart,
    'culinary': ChefHat,
    'community': Users,
    'creative': Palette,
    'family': Users,
    'shop': ShoppingBag,
    'extreme-sports': Flame
  }
  return iconMap[themeId] || Mountain
}

const getTravelMethodIcon = (methodId) => {
  const iconMap = {
    'car': Car,
    'motorbike': Bike,
    'bicycle': Bike,
    'campervan': Car,
    'air': Plane,
    'train': Train,
    'coach': Bus,
    'yacht': Ship,
    'cruise': Ship
  }
  return iconMap[methodId] || Car
}

const getAccommodationIcon = (accommodationId) => {
  const iconMap = {
    'hotel': Hotel,
    'motel': Hotel,
    'airbnb': Home,
    'homestay': Home,
    'farmstay': TreePine,
    'eco-lodge': TreePine,
    'hostel': Backpack,
    'boat': Ship,
    'campsite': Tent,
    'cabin': Home
  }
  return iconMap[accommodationId] || Home
}

const getSpecialRequirementIcon = (requirementId) => {
  const iconMap = {
    'wheelchair-access': User,
    'service-animal-friendly': PawPrint,
    'pet-friendly': PawPrint
  }
  return iconMap[requirementId] || User
}

const props = defineProps({
  // Controls visibility of the whole block via parent condition
  show: { type: Boolean, default: true },
  // Accordion open state
  open: { type: Boolean, default: false },
  // Options
  themeOptions: { type: Array, default: () => [] },
  travelMethodOptions: { type: Array, default: () => [] },
  accommodationOptions: { type: Array, default: () => [] },
  specialRequirementsOptions: { type: Array, default: () => [] },
  // Selections (v-model arrays)
  selectedThemes: { type: Array, default: () => [] },
  travelersData: { type: Object, default: () => ({ adults: 0, children: 0, pets: 0 }) },
  selectedTravelMethods: { type: Array, default: () => [] },
  selectedAccommodations: { type: Array, default: () => [] },
  selectedSpecialRequirements: { type: Array, default: () => [] },
  selectedActivities: { type: Array, default: () => [] },
  tripDirection: { type: String, default: 'return' },
  isSearchEnabled: { type: Boolean, default: false }
})

const emit = defineEmits([
  'update:open',
  'update:selectedThemes',
  'update:selectedTravelMethods',
  'update:selectedAccommodations',
  'update:selectedSpecialRequirements',
  'update:selectedActivities',
  'search',
  'validation-change',
  'update:tripDirection'
])

const open = computed({
  get: () => props.open,
  set: (val) => emit('update:open', val)
})


const localSelectedActivities = computed({
  get: () => props.selectedActivities,
  set: (val) => emit('update:selectedActivities', val)
})


// Computed property to check if any filters are active in the Refine Budget section
const hasActiveFilters = computed(() => {
  return (
    props.selectedActivities.length > 0 ||
    props.selectedTravelMethods.length > 0 ||
    props.selectedAccommodations.length > 0 ||
    props.selectedSpecialRequirements.length > 0 ||
    props.tripDirection === 'oneway'
  )
})

const totalSelectedCount = computed(() => {
  let count = 0
  count += props.selectedActivities.length
  count += props.selectedAccommodations.length
  count += props.selectedTravelMethods.length
  if (props.tripDirection === 'oneway') count++
  return count
})


const lists = {
  themes: {
    getter: () => props.selectedThemes,
    emitter: (val) => emit('update:selectedThemes', val)
  },
  travelMethods: {
    getter: () => props.selectedTravelMethods,
    emitter: (val) => emit('update:selectedTravelMethods', val)
  },
  accommodations: {
    getter: () => props.selectedAccommodations,
    emitter: (val) => emit('update:selectedAccommodations', val)
  },
  specialRequirements: {
    getter: () => props.selectedSpecialRequirements,
    emitter: (val) => emit('update:selectedSpecialRequirements', val)
  }
}

const toggleFromList = (listName, id) => {
  const current = lists[listName].getter()
  const next = current.includes(id)
    ? current.filter((x) => x !== id)
    : [...current, id]
  lists[listName].emitter(next)
}

const removeFromList = (listName, id) => {
  const current = lists[listName].getter()
  lists[listName].emitter(current.filter((x) => x !== id))
}

const isSelected = (listName, id) => {
  return lists[listName].getter().includes(id)
}

const toggleOpen = () => {
  open.value = !open.value
}

const handleSearch = () => {
  emit('search')
}

const clearAll = () => {
  // Clear all selection arrays
  emit('update:selectedThemes', [])
  emit('update:selectedTravelMethods', [])
  emit('update:selectedAccommodations', [])
  emit('update:selectedSpecialRequirements', [])
  emit('update:selectedActivities', [])
  emit('update:tripDirection', 'return')
}


const localTripDirection = computed({
  get: () => props.tripDirection,
  set: (val) => emit('update:tripDirection', val)
})

</script>

<style scoped>
/* Staggered animation for sections */
.stagger-enter-from {
  opacity: 0;
  transform: translateY(20px);
}
.stagger-enter-active {
  transition: all 0.4s ease-out;
}
.stagger-enter-to {
  opacity: 1;
  transform: translateY(0);
}
.stagger-leave-from {
  opacity: 1;
  transform: translateY(0);
}
.stagger-leave-active {
  transition: all 0.3s ease-in;
}
.stagger-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}
</style>
